import { Link } from "@tanstack/react-router";
import { StatusPill, type StatusTone } from "@/components/shared/status-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { type CertificationPersonRow, useCertificationPeople } from "@/hooks/use-certifications";
import { isExpiredDateOnly } from "@/lib/dates";
import { originLinkSearch } from "@/lib/detail-origin";
import { formatDate } from "@/lib/utils";

const statusToneMap: Record<CertificationPersonRow["status"], StatusTone> = {
  assigned: "neutral",
  started: "info",
  completed: "success",
};

/** The milestone worth showing for the row's current stage. */
function milestone(row: CertificationPersonRow): string {
  if (row.status === "completed" && row.completedAt)
    return `Completed ${formatDate(row.completedAt)}`;
  if (row.status === "started" && row.startedAt) return `Started ${formatDate(row.startedAt)}`;
  return `Assigned ${formatDate(row.assignedAt)}`;
}

/**
 * Everyone this certification is assigned to — grouped assigned → started →
 * completed by the API — each linking to their person page. Links carry the
 * certification (and its "people" tab) as origin so the person page can
 * breadcrumb straight back to this list.
 */
export function CertificationPeopleSection({
  certificationId,
  certificationName,
}: {
  certificationId: string;
  certificationName: string;
}) {
  const { data = [], isLoading, isError } = useCertificationPeople(certificationId);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (isError) {
    return <p className="text-sm text-muted-foreground">Unable to load people.</p>;
  }

  if (data.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No one has been assigned this certification yet.
      </p>
    );
  }

  return (
    <ul className="space-y-2">
      {data.map((row) => {
        const expired = isExpiredDateOnly(row.expiresAt);
        return (
          <li key={row.id}>
            <Link
              to="/dashboard/persons/$personId"
              params={{ personId: row.personId }}
              search={originLinkSearch(
                "certification",
                certificationId,
                certificationName,
                "people",
              )}
              className="flex items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-sm transition-colors hover:bg-accent"
            >
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-medium text-foreground">{row.personName}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {row.personTitle ? `${row.personTitle} · ` : ""}
                  {milestone(row)}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-1.5">
                {expired && <StatusPill tone="danger" label="Expired" />}
                <StatusPill
                  tone={statusToneMap[row.status]}
                  label={row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                />
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
